'use client';

import { ReactNode, useState } from 'react';
import { useParams } from 'next/navigation';
import { WithdrawalSummaryDrawer } from '../../../Fiat/Withdrawal/WithdrawalSummary/WithdrawalSummaryDrawer';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import Link from 'next/link';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { FilledDismiss } from '../../../../common/svgIcons';
import { getAppConfig } from '@bts-web/core-app-config';
import { useClientUserInfo } from '../../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { useModalDrawerContext } from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { TriggerSetupTwoFaModal } from '../../../../common/components/composableModals';
import { AutomaticWithdrawalsContext } from '../AutomaticWithdrawalsContext';
import { AddSavingsPlanToggle } from '../subcomponents/AddSavingsPlanToggle/AddSavingsPlanToggle.use-client';
import { getAssetTradeUrl } from '../../../../route-builders/getAssetTradeUrl';
import { FinishTradeBody } from './FinishTradeBody';
import {
  containerWrapperStyles,
  containerStyles,
  headerStyles,
  closeLinkStyles,
} from './finishTradeContentDisplayStyles';
import { getFiatWalletWithAccountInfo } from '@bts-web/core-features/common';
import { WithdrawalAmountDrawer } from '../../../Fiat/Withdrawal/WithdrawalAmount/WithdrawalAmountDrawer';

export interface FinishTradeContentDisplayProps {
  assetId: string;
  assetLogoUrl: string;
  assetName: string;
  isSell?: boolean;
  isBuy?: boolean;
  sellFiatAmount?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  statusContent?: ReactNode;
  statusTitleIcon?: ReactNode;
  poweredByText?: string;
  tradeId?: string;
  primaryButton?: {
    text: string;
    url: string;
    replace?: boolean;
  };
  secondaryButton?: {
    text: string;
    url: string;
  };
  onClose?: () => void;
  enableFiatTransfers?: boolean;
  hasSavingsPlansEnabled?: boolean;
  isBpfs: boolean;
}

export const FinishTradeContentDisplay = ({
  assetId,
  assetLogoUrl,
  assetName,
  isSell,
  isBuy,
  sellFiatAmount,
  title,
  subtitle,
  statusContent,
  statusTitleIcon,
  tradeId,
  poweredByText,
  primaryButton,
  secondaryButton,
  onClose,
  enableFiatTransfers,
  hasSavingsPlansEnabled,
  isBpfs,
}: FinishTradeContentDisplayProps) => {
  const { t } = useClientTranslation();

  const { automaticWithdrawalsDefaultEnabled } = getAppConfig();

  const { twoFactorAuthentication } = useClientUserInfo();

  const { showModal, openDrawer } = useModalDrawerContext();

  const [isEnabled, setIsEnabled] = useState(
    automaticWithdrawalsDefaultEnabled ?? false,
  );

  const [isAddSavingsPlanEnabled, setIsAddSavingsPlanEnabled] = useState(false);

  const [isHidden, setIsHidden] = useState(false);

  const params = useParams<{ offerId?: string }>();

  const showWithdrawal =
    isSell && isEnabled && !!params.offerId && !!enableFiatTransfers;

  const savingsPlanUrl = getAssetTradeUrl(assetId, { type: 'savings' });

  const showSavingsPlan = isBuy && isAddSavingsPlanEnabled;

  const handleWithdrawClick = async () => {
    if (twoFactorAuthentication !== 'ENABLED') {
      showModal(TriggerSetupTwoFaModal, { disableOverlayClose: true });

      return;
    }

    const walletResponse = await getFiatWalletWithAccountInfo();

    if (
      Number(walletResponse.data.withdrawalBalance) <
      Number(sellFiatAmount || 0)
    ) {
      openDrawer(WithdrawalAmountDrawer, {
        initialInputAmount: walletResponse.data.withdrawalBalance,
        fromSell: true,
      });
    } else {
      openDrawer(WithdrawalSummaryDrawer, {
        amount: sellFiatAmount ?? '',
        showFee: true,
        tradeId: tradeId,
      });
    }

    setIsHidden(true);
  };

  const primaryButtonText = showWithdrawal
    ? t('confirm')
    : showSavingsPlan
      ? t('savings_plan')
      : primaryButton?.text;

  const effectivePrimaryButton =
    showSavingsPlan && primaryButton
      ? { ...primaryButton, url: savingsPlanUrl }
      : primaryButton;

  const enrichedStatusContent =
    isBuy && hasSavingsPlansEnabled ? (
      <>
        {statusContent}

        <AddSavingsPlanToggle
          isEnabled={isAddSavingsPlanEnabled}
          onToggle={setIsAddSavingsPlanEnabled}
        />
      </>
    ) : (
      statusContent
    );

  return (
    <AutomaticWithdrawalsContext.Provider value={{ isEnabled, setIsEnabled }}>
      <div
        data-testid="finish-trade-wrapper"
        className={containerWrapperStyles}
        style={isHidden ? { display: 'none' } : undefined}
      >
        <div className={containerStyles}>
          <div data-testid="finish-trade-header" className={headerStyles}>
            <Link
              data-testid="finish-trade-close"
              href={`${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId)}?ref=portfolio`}
              className={closeLinkStyles}
              onClick={onClose}
            >
              <FilledDismiss size={24} />
            </Link>
          </div>

          <FinishTradeBody
            assetLogoUrl={assetLogoUrl}
            assetName={assetName}
            title={title}
            subtitle={subtitle}
            statusContent={enrichedStatusContent}
            statusTitleIcon={statusTitleIcon}
            poweredByText={poweredByText}
            primaryButton={effectivePrimaryButton}
            secondaryButton={secondaryButton}
            primaryButtonText={primaryButtonText}
            onPrimaryClick={showWithdrawal ? handleWithdrawClick : undefined}
            isBpfs={isBpfs}
          />
        </div>
      </div>
    </AutomaticWithdrawalsContext.Provider>
  );
};
